import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { UserRole, Client } from '../../types';
import * as api from '../../services/api';
import { useUnreadMessages } from '../../contexts/UnreadMessagesContext';
import { CrownIcon, LogOutIcon } from '../ui/icons';
import { 
  ChevronDown, 
  ChevronRight, 
  LayoutDashboard, 
  Users, 
  CheckSquare, 
  Layers, 
  Calendar, 
  Timer, 
  Target, 
  MessageSquare, 
  Headphones, 
  BookOpen, 
  ClipboardCheck,
  Coins, 
  Shield, 
  FolderOpen, 
  Settings, 
  Archive, 
  UserCircle,
  Compass
} from 'lucide-react';

interface NavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  badgeCount?: number;
}

const NavItem: React.FC<NavItemProps> = ({ to, icon, label, onClick, badgeCount }) => {
  const location = useLocation();
  const isMatch = (to === '/check-in' || to === '/reports') && (location.pathname === '/check-in' || location.pathname === '/reports');

  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        `flex items-center justify-between px-3.5 py-2.5 rounded-xl transition-all duration-200 group active:scale-[0.98] select-none ${
          (isActive || isMatch)
          ? 'bg-primary/15 text-text-main font-semibold border border-primary/40 shadow-glow shadow-primary/10' 
          : 'hover:bg-surface-light/60 hover:text-text-main text-text-secondary border border-transparent'
        }`
      }
    >
      <div className="flex items-center space-x-3 min-w-0">
        <span className="w-5 h-5 flex items-center justify-center text-primary group-hover:scale-110 transition-transform shrink-0">
          {icon}
        </span>
        <span className="text-xs font-medium tracking-wide truncate">{label}</span>
      </div>
      {badgeCount !== undefined && badgeCount > 0 && (
        <span className="bg-rose-500 text-white text-[10px] font-bold rounded-full h-5 min-w-[20px] px-1.5 flex items-center justify-center shadow-md animate-pulse shrink-0">
          {badgeCount > 9 ? '9+' : badgeCount}
        </span>
      )}
    </NavLink>
  );
};

const NavSectionHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="px-3.5 pt-4 pb-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-primary/70 select-none flex items-center gap-1.5">
    <span>{children}</span>
  </div>
);

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const { totalUnreadCount } = useUnreadMessages();
  const isDomina = user?.role === UserRole.DOMINA;
  
  const [clients, setClients] = useState<Client[]>([]);
  const [controlActive, setControlActive] = useState(false);
  const [isClientsExpanded, setIsClientsExpanded] = useState(false);

  useEffect(() => {
    const checkControl = async () => {
      if (!isDomina && user?.clientId) {
        try {
          const config = await api.getControlConfigByClientId(user.clientId);
          setControlActive(!!config && config.isActive);
        } catch (error) {
          console.error("Failed to fetch control status:", error);
        }
      }
    };
    checkControl();
  }, [user?.clientId, isDomina]);

  useEffect(() => {
    if (location.pathname.startsWith('/clients')) {
      setIsClientsExpanded(true);
    }
  }, [location.pathname]);
  
  const fetchClients = useCallback(async () => {
    if (isDomina) {
      try {
        const fetchedClients = await api.getClients();
        setClients(fetchedClients);
      } catch (error) {
        console.error("Failed to fetch clients for sidebar:", error);
      }
    }
  }, [isDomina]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const activeClients = useMemo(() => {
    return clients
      .filter(c => c.isActive && !c.isPaused)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [clients]);

  return (
    <>
      {/* Mobile backdrop */}
      <div 
        className={`fixed inset-0 bg-black/70 backdrop-blur-sm z-40 md:hidden transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
        aria-hidden="true"
      />

      <aside className={`fixed inset-y-0 left-0 w-72 bg-surface/95 backdrop-blur-2xl border-r border-border/50 flex flex-col transform transition-transform duration-300 ease-out z-50 md:relative md:translate-x-0 shadow-2xl ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        
        {/* Brand Header */}
        <div className="h-18 flex items-center justify-between px-5 border-b border-border/40 bg-surface-light/30">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-primary/15 border border-primary/30 flex items-center justify-center shadow-glow shadow-primary/20 shrink-0">
              <CrownIcon className="w-5 h-5 text-accent" />
            </div>
            <div>
              <h1 className="text-base font-bold text-text-main font-heading tracking-wider">Fairyrealm</h1>
              <p className="text-[10px] text-text-secondary uppercase tracking-widest font-mono">
                {isDomina ? 'Göttin Cat' : 'Klienten-Portal'}
              </p>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 md:hidden text-text-secondary hover:text-text-main active:scale-95 transition-transform rounded-lg hover:bg-surface-light"
            aria-label="Menü schließen"
          >
            <span className="text-2xl leading-none">&times;</span>
          </button>
        </div>
        
        {/* Navigation Content */}
        <nav className="flex-1 px-3 py-3 space-y-1 overflow-y-auto scrollbar-thin scrollbar-thumb-primary/20 hover:scrollbar-thumb-primary/40">
          {isDomina ? (
            <>
              {/* 1. ÜBERSICHT */}
              <NavSectionHeader>Übersicht</NavSectionHeader>
              <NavItem to="/" icon={<LayoutDashboard className="w-4 h-4" />} label="Dashboard" onClick={onClose} />

              {/* 2. KLIENTEN */}
              <NavSectionHeader>Klienten</NavSectionHeader>
              <div className="space-y-0.5">
                <NavItem to="/clients" icon={<Users className="w-4 h-4" />} label="Klientenübersicht" onClick={onClose} />
                
                {/* Collapsible Active Clients */}
                {activeClients.length > 0 && (
                  <div className="pt-0.5">
                    <button
                      onClick={() => setIsClientsExpanded(prev => !prev)}
                      className="flex items-center justify-between w-full px-3.5 py-1.5 rounded-lg text-text-secondary hover:text-text-main hover:bg-surface-light/40 transition-colors text-xs"
                    >
                      <span className="text-[11px] font-medium tracking-wide">Aktive Akten ({activeClients.length})</span>
                      {isClientsExpanded ? <ChevronDown className="w-3.5 h-3.5 opacity-60" /> : <ChevronRight className="w-3.5 h-3.5 opacity-60" />}
                    </button>
                    
                    {isClientsExpanded && (
                      <div className="pl-3 mt-1 space-y-0.5 border-l border-primary/20 ml-4 mb-2">
                        {activeClients.map(client => (
                          <NavLink
                            key={client.id}
                            to={`/clients/${client.id}`}
                            onClick={onClose}
                            className={({ isActive }) =>
                              `flex items-center space-x-2.5 px-2.5 py-1.5 rounded-lg text-xs transition-all ${
                                isActive 
                                  ? 'text-primary font-bold bg-primary/10 border-l-2 border-primary' 
                                  : 'hover:text-text-main text-text-secondary hover:bg-surface-light/40'
                              }`
                            }
                          >
                            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${client.isPaused ? 'bg-amber-500' : 'bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.5)]'}`} />
                            <span className="truncate">{client.name}</span>
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* 3. AUFGABEN & PLANUNG */}
              <NavSectionHeader>Aufgaben & Planung</NavSectionHeader>
              <NavItem to="/coaching" icon={<Compass className="w-4 h-4" />} label="Coaching" onClick={onClose} />
              <NavItem to="/tasks" icon={<CheckSquare className="w-4 h-4" />} label="Aufgaben" onClick={onClose} />
              <NavItem to="/structure" icon={<Layers className="w-4 h-4" />} label="Struktur & Routinen" onClick={onClose} />
              <NavItem to="/calendar" icon={<Calendar className="w-4 h-4" />} label="Kalender" onClick={onClose} />
              <NavItem to="/timer" icon={<Timer className="w-4 h-4" />} label="Timer & Wecker" onClick={onClose} />
              <NavItem to="/control" icon={<Target className="w-4 h-4" />} label="Zielmatrix" onClick={onClose} />

              {/* 4. INTERAKTION */}
              <NavSectionHeader>Interaktion</NavSectionHeader>
              <NavItem to="/messenger" icon={<MessageSquare className="w-4 h-4" />} label="Messenger" onClick={onClose} badgeCount={totalUnreadCount} />
              <NavItem to="/audio-inputs" icon={<Headphones className="w-4 h-4" />} label="Flüsterraum" onClick={onClose} />
              <NavItem to="/diary" icon={<BookOpen className="w-4 h-4" />} label="Tagebuch" onClick={onClose} />
              <NavItem to="/check-in" icon={<ClipboardCheck className="w-4 h-4" />} label="Check-In" onClick={onClose} />

              {/* 5. VERWALTUNG */}
              <NavSectionHeader>Verwaltung</NavSectionHeader>
              <NavItem to="/payments" icon={<Coins className="w-4 h-4" />} label="Zahlungen" onClick={onClose} />
              <NavItem to="/rules" icon={<Shield className="w-4 h-4" />} label="Regelwerke" onClick={onClose} />
              <NavItem to="/media" icon={<FolderOpen className="w-4 h-4" />} label="Mediathek" onClick={onClose} />
            </>
          ) : (
            <>
              {/* SUB CLIENT NAVIGATION */}
              <NavSectionHeader>Übersicht</NavSectionHeader>
              <NavItem to="/" icon={<LayoutDashboard className="w-4 h-4" />} label="Dashboard" onClick={onClose} />
              <NavItem to="/coaching" icon={<Compass className="w-4 h-4" />} label="Coaching" onClick={onClose} />

              <NavSectionHeader>Aufgaben & Struktur</NavSectionHeader>
              <NavItem to="/tasks" icon={<CheckSquare className="w-4 h-4" />} label="Meine Aufgaben" onClick={onClose} />
              <NavItem to="/planning" icon={<Layers className="w-4 h-4" />} label="Tagesstruktur" onClick={onClose} />
              <NavItem to="/calendar" icon={<Calendar className="w-4 h-4" />} label="Kalender" onClick={onClose} />
              <NavItem to="/timer" icon={<Timer className="w-4 h-4" />} label="Timer & Wecker" onClick={onClose} />
              {controlActive && (
                <NavItem to="/control" icon={<Target className="w-4 h-4" />} label="Zielmatrix" onClick={onClose} />
              )}

              <NavSectionHeader>Interaktion</NavSectionHeader>
              <NavItem to="/messenger" icon={<MessageSquare className="w-4 h-4" />} label="Messenger" onClick={onClose} badgeCount={totalUnreadCount} />
              <NavItem to="/audio-inputs" icon={<Headphones className="w-4 h-4" />} label="Flüsterraum" onClick={onClose} />
              <NavItem to="/diary" icon={<BookOpen className="w-4 h-4" />} label="Tagebuch" onClick={onClose} />
              <NavItem to="/check-in" icon={<ClipboardCheck className="w-4 h-4" />} label="Check-In" onClick={onClose} />

              <NavSectionHeader>Wissen & Regeln</NavSectionHeader>
              <NavItem to="/rules" icon={<Shield className="w-4 h-4" />} label="Regeln der Göttin" onClick={onClose} />
              <NavItem to="/media" icon={<FolderOpen className="w-4 h-4" />} label="Mediathek" onClick={onClose} />
              <NavItem to="/archive" icon={<Archive className="w-4 h-4" />} label="Archiv" onClick={onClose} />

              <NavSectionHeader>Persönlich</NavSectionHeader>
              <NavItem to="/profile" icon={<UserCircle className="w-4 h-4" />} label="Mein Profil" onClick={onClose} />
            </>
          )}
        </nav>
        
        {/* Footer Area: Settings & Logout */}
        <div className="p-3 border-t border-border/40 bg-surface-light/20 space-y-1">
          <NavItem to="/settings" icon={<Settings className="w-4 h-4" />} label="Einstellungen" onClick={onClose} />
          
          <button
            onClick={() => {
              onClose();
              logout();
            }}
            className="flex items-center justify-between w-full px-3.5 py-2.5 rounded-xl hover:bg-rose-500/10 text-text-secondary hover:text-rose-400 transition-all duration-200 cursor-pointer active:scale-[0.98] border border-transparent group select-none"
          >
            <div className="flex items-center space-x-3">
              <span className="w-5 h-5 flex items-center justify-center text-text-secondary group-hover:text-rose-400 group-hover:scale-110 transition-transform">
                <LogOutIcon className="w-4 h-4" />
              </span>
              <span className="text-xs font-medium tracking-wide">Abmelden</span>
            </div>
          </button>
        </div>
      </aside>
    </>
  );
};
